import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { useQueryParamsEffect, useQueryParamTerm } from 'pricing-pages-ui/utils/urls/queryParamUtils';
import Screens from '../../constants/routing/Screens';
import { usePageParams } from '../../hooks/usePageParams';
import { useAnchorLinkScroll } from '../../hooks/htmlElementHooks';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useTrackRetail } from '../../hooks/useTrackRetail';
import { hasOnlyOwnershipCheckFailedSelector, hasPricingAndPurchasingFailedSelector } from '../../selectors/LoadingStateSelectors';
import HeaderLayout from '../layout/HeaderLayout';
import FailedLoadBanner from '../common/loading/FailedLoadBanner';
import FallbackErrorBanner from '../common/loading/FallbackErrorBanner';
import ProductInfoContainer from '../common/ProductInfoContainer';
import ProductPageTitle, { ProductPageHeader } from './ProductPageTitle';
import ProductPageSubtitle from './ProductPageSubtitle';
import { ProductCalculator } from '../calculator/ProductCalculator';
import Faq from '../faqs/Faq';
import { FaqsConfig } from '../../constants/faqs/FaqsConfig';
import Question from '../faqs/Question';
import FooterMicrocopy from '../faqs/FooterMicrocopy';
import ValuePropsSection from '../common/ValuePropsSection';
import { getHighlightsForProductLine, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import FeatureTable from '../feature-table/FeatureTable';
import HubTierInfo from '../merch-card/HubTierInfo';
import CompareStatusFooter from '../compare/CompareStatusFooter';
import CodeSplitEnvWrapper from '../common/env/CodeSplitEnvWrapper';
import { getMerchCardsToShow } from '../../constants/merchCards/MerchCardsToShow';
import IntegrationsSection from '../common/Integrations';
import BannerSection from '../banners/BannerSection';
import { ProductPageContext } from '../../context/ProductPageContext';
import StarterCrmSuiteCtaCard from './StarterCrmSuiteCtaCard';
import { STARTER } from 'pricing-pages-ui/constants/routing/Tabs';
import { useBusinessObjectives } from '../../hooks/useBusinessObjectives';
import PhoneSupportFooter from '../common/PhoneSupportFooter';
import PhoneSupport from '../common/PhoneSupport';
import { isAnyStarterGWP2023EligibleSelector } from '../../selectors/PromotionSelectors';
import { OWNERSHIP_CHECK_FAILED } from '../../constants/ErrorCauses';
import ErrorPage from '../error-page/ErrorPage';

const ProductPage = ({
  productLine
}) => {
  useAnchorLinkScroll();
  useTrackRetail();
  useDocumentTitle(productLine.toLowerCase());
  const hasPricingAndPurchasingFailed = useSelector(hasPricingAndPurchasingFailedSelector);
  const hasOnlyOwnershipCheckFailed = useSelector(hasOnlyOwnershipCheckFailedSelector);
  const isStarterGWP = useSelector(isAnyStarterGWP2023EligibleSelector);
  const {
    tab
  } = usePageParams();
  const shouldDisplaySuiteCard = tab === STARTER && !isStarterGWP;
  useEffect(() => {
    getUsageTracker().track('pageView', {
      screen: Screens[productLine],
      tab
    });
  }, [productLine, tab]);
  const highlightedProducts = getHighlightsForProductLine(productLine);
  const productLevels = getMerchCardsToShow({
    tab
  });
  const filteredHighlightedProducts = highlightedProducts.filter(apiName => {
    const localProductLevel = getProductLevelFromApiNameString(apiName);
    return productLevels.includes(localProductLevel);
  });
  useBusinessObjectives({
    highlightedProducts: filteredHighlightedProducts
  });
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });

  if (hasPricingAndPurchasingFailed) {
    if (hasOnlyOwnershipCheckFailed) {
      return /*#__PURE__*/_jsx(ErrorPage, {
        errorCause: OWNERSHIP_CHECK_FAILED
      });
    }
  }

  const screen = productLine.toLowerCase();
  return /*#__PURE__*/_jsxs("div", {
    "data-selenium-id": "page-success",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: `${productLine}_PAGE`
    }), /*#__PURE__*/_jsx(HeaderLayout, {
      selectedNavItem: productLine.toLowerCase(),
      children: /*#__PURE__*/_jsx(ProductInfoContainer, {
        children: /*#__PURE__*/_jsxs(ProductPageContext.Provider, {
          value: {
            highlightedProducts: filteredHighlightedProducts
          },
          children: [/*#__PURE__*/_jsx(BannerSection, {
            productLine: productLine,
            screen: screen
          }), /*#__PURE__*/_jsx(FailedLoadBanner, {
            hasFailed: hasPricingAndPurchasingFailed
          }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(ProductPageHeader, {
            children: [/*#__PURE__*/_jsx(ProductPageTitle, {
              productLine: productLine
            }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
              productLine: productLine
            })]
          }), /*#__PURE__*/_jsx(HubTierInfo, {
            productLine: productLine,
            selectedTab: tab
          }), /*#__PURE__*/_jsx(FeatureTable, {
            productLine: productLine,
            highlightedProducts: filteredHighlightedProducts,
            screen: Screens[productLine],
            tab: tab
          }, `${productLine}-${tab}`), shouldDisplaySuiteCard && /*#__PURE__*/_jsx(StarterCrmSuiteCtaCard, {
            productLine: productLine
          }), /*#__PURE__*/_jsx(PhoneSupport, {
            phoneSourceId: `${productLine.toLowerCase()}-middle`,
            screen: Screens[productLine]
          }), /*#__PURE__*/_jsx(ProductCalculator, {
            productLine: productLine,
            screen: Screens[productLine]
          }), /*#__PURE__*/_jsx(IntegrationsSection, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import(
            /* webpackChunkName: "public-chunk" */
            `pricing-pages-ui/components/feature-modal/FeatureCardWrapper`),
            publicComponentProps: {
              productLine
            }
          }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import(
            /* webpackChunkName: "public-chunk" */
            `pricing-pages-ui/components/company-proof/CompanyProof`),
            publicComponentProps: {
              tab
            }
          }), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
            publicComponentLoader: () => import(
            /* webpackChunkName: "public-chunk" */
            `pricing-pages-ui/components/case-studies/CaseStudies`),
            publicComponentProps: {
              productLine,
              tab
            }
          }), /*#__PURE__*/_jsx(ValuePropsSection, {}), /*#__PURE__*/_jsxs(Faq, {
            children: [FaqsConfig[productLine.toLowerCase()].map((_ref) => {
              let {
                questionKey
              } = _ref,
                  passthroughOptions = _objectWithoutPropertiesLoose(_ref, ["questionKey"]);

              return /*#__PURE__*/_jsx(Question, Object.assign({
                questionKey: questionKey
              }, passthroughOptions), questionKey);
            }), /*#__PURE__*/_jsx(FooterMicrocopy, {})]
          }), /*#__PURE__*/_jsx(PhoneSupportFooter, {
            phoneSourceId: `${productLine.toLowerCase()}-footer`,
            screen: Screens[productLine]
          })]
        })
      })
    }), /*#__PURE__*/_jsx(CompareStatusFooter, {})]
  });
};

export default ProductPage;